import type { ElementType } from './types.ts';

/** Shared skeleton families. Creatures in one archetype share a bone hierarchy (docs/03). */
export type Archetype = 'quadruped' | 'biped' | 'avian' | 'serpentine' | 'floater';

export interface BaseStats {
  /** Vitality: max HP = 200 + 6 x vit */
  vit: number;
  /** Strength: physical damage */
  str: number;
  /** Focus: elemental damage */
  foc: number;
  /** Guard: physical defense */
  grd: number;
  /** Resolve: elemental defense, slows status buildup */
  res: number;
  /** Agility: move speed, dodge distance and recovery */
  agi: number;
}

export interface CreatureDef {
  id: string;
  name: string;
  types: readonly [ElementType] | readonly [ElementType, ElementType];
  archetype: Archetype;
  role: string;
  concept: string;
  stats: BaseStats;
  /** Part of the vertical slice (M4). */
  slice: boolean;
}

export const BASE_STAT_TOTAL = 540;

export function maxHp(stats: BaseStats): number {
  return 200 + 6 * stats.vit;
}

export function statTotal(stats: BaseStats): number {
  return stats.vit + stats.str + stats.foc + stats.grd + stats.res + stats.agi;
}

const s = (vit: number, str: number, foc: number, grd: number, res: number, agi: number) => ({
  vit,
  str,
  foc,
  grd,
  res,
  agi,
});

/** Roster v1. Names are working names pending trademark and Pokedex checks (docs/03). */
export const CREATURES: readonly CreatureDef[] = [
  {
    id: 'kilnback',
    name: 'Kilnback',
    types: ['flame'],
    archetype: 'quadruped',
    role: 'Bruiser / roller',
    concept: 'Pangolin with kiln-hot ceramic scales and magma seams; curls into a burning wheel.',
    stats: s(105, 115, 70, 110, 75, 65),
    slice: true,
  },
  {
    id: 'rillwing',
    name: 'Rillwing',
    types: ['tide', 'gale'],
    archetype: 'avian',
    role: 'Zoner / mage',
    concept: 'Storm heron with a rain-cloud crest and water-orb wingtips.',
    stats: s(80, 60, 120, 65, 95, 120),
    slice: true,
  },
  {
    id: 'mosslumber',
    name: 'Mosslumber',
    types: ['verdant'],
    archetype: 'biped',
    role: 'Tank / controller',
    concept: 'Giant sloth with a moss-garden coat; hangs from its own vines.',
    stats: s(125, 90, 85, 100, 100, 40),
    slice: false,
  },
  {
    id: 'arcquill',
    name: 'Arcquill',
    types: ['volt'],
    archetype: 'quadruped',
    role: 'Skirmisher / burst',
    concept: 'Porcupine whose quills store static and fire as bolts.',
    stats: s(70, 75, 115, 70, 80, 130),
    slice: false,
  },
  {
    id: 'rimehorn',
    name: 'Rimehorn',
    types: ['frost', 'brawl'],
    archetype: 'quadruped',
    role: 'Juggernaut',
    concept: 'Musk ox with a glacier-horn helm and snow-squall mane.',
    stats: s(115, 120, 65, 105, 80, 55),
    slice: false,
  },
  {
    id: 'orbitite',
    name: 'Orbitite',
    types: ['stone', 'mind'],
    archetype: 'floater',
    role: 'Controller',
    concept: 'Crystal core orbited by runed rock shards it rearranges into shields and spears.',
    stats: s(90, 70, 110, 115, 105, 50),
    slice: false,
  },
  {
    id: 'draftling',
    name: 'Draftling',
    types: ['gale', 'brawl'],
    archetype: 'quadruped',
    role: 'Aerial rushdown',
    concept: 'Draco gliding lizard with sail-membranes; dive-kicks from the air.',
    stats: s(75, 115, 60, 70, 75, 145),
    slice: false,
  },
  {
    id: 'blightfrill',
    name: 'Blightfrill',
    types: ['venom', 'tide'],
    archetype: 'serpentine',
    role: 'Area denial / trapper',
    concept: 'Giant nudibranch sea-slug serpent with toxic neon frills.',
    stats: s(95, 60, 105, 80, 115, 85),
    slice: false,
  },
  {
    id: 'tarsage',
    name: 'Tarsage',
    types: ['mind'],
    archetype: 'biped',
    role: 'Caster / trickster',
    concept: 'Tarsier sage with enormous glowing eyes; levitates cross-legged.',
    stats: s(70, 45, 135, 65, 110, 115),
    slice: false,
  },
  {
    id: 'gloamstag',
    name: 'Gloamstag',
    types: ['shade', 'verdant'],
    archetype: 'quadruped',
    role: 'Assassin',
    concept: 'Twilight stag with shadow-antlers that sprout night-blooming flowers.',
    stats: s(85, 120, 80, 75, 70, 110),
    slice: false,
  },
  {
    id: 'anvilosaur',
    name: 'Anvilosaur',
    types: ['iron', 'stone'],
    archetype: 'quadruped',
    role: 'Tank / bruiser',
    concept: 'Ankylosaur with forged-iron plates and an anvil club tail.',
    stats: s(110, 105, 50, 140, 90, 45),
    slice: false,
  },
  {
    id: 'stomatide',
    name: 'Stomatide',
    types: ['brawl', 'tide'],
    archetype: 'biped',
    role: 'Rushdown',
    concept: 'Mantis-shrimp martial artist with a prismatic carapace and cavitation punches.',
    stats: s(85, 130, 55, 90, 70, 110),
    slice: false,
  },
];

export function creatureById(id: string): CreatureDef | undefined {
  return CREATURES.find((c) => c.id === id);
}
